/**
 * Кабель на плане: ломаная по потолку от прибора к прибору — компонент раздела «Кабель».
 *
 * Рисуется поверх стен и мебели тонкой линией в цвет проводки, концы отмечены
 * точками — это места спуска к приборам. Черновик кабеля (CableDraft) — та же
 * линия пунктиром плюс отрезок к курсору: видно, куда ляжет следующий участок.
 * Толщина линий — в пикселях экрана, чтобы кабель не жирнел на зуме.
 */
import type { PointerEvent } from "react";
import type { Pt } from "@/shared/lib/geometry";
const SELECTED_COLOR = "#2f6feb";

const toPoints = (route: Pt[]) => route.map((p) => `${p.x},${p.y}`).join(" ");

interface Props {
	/** Полная трасса: от прибора через точки к прибору */
	route: Pt[];
	selected: boolean;
	mmPerPx: number;
	interactive?: boolean;
	cableId: string;
	color: string;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

export const CableShape = ({
	route,
	selected,
	mmPerPx,
	interactive = true,
	cableId,
	color,
	onPointerDown,
}: Props) => {
	const px = (n: number) => n * mmPerPx;
	const ends = [route[0], route[route.length - 1]];
	return (
		<g
			onPointerDown={onPointerDown}
			style={{
				cursor: interactive ? "pointer" : "default",
				pointerEvents: interactive ? "auto" : "none",
			}}
		>
			{selected && (
				<polyline
					points={toPoints(route)}
					fill="none"
					stroke="#2f6feb33"
					strokeWidth={px(7)}
					strokeLinejoin="round"
					strokeLinecap="round"
				/>
			)}
			<polyline
				points={toPoints(route)}
				fill="none"
				stroke={color}
				strokeWidth={px(1.7)}
				strokeLinejoin="round"
				strokeLinecap="round"
			/>
			{ends.map((p, i) => (
				<circle
					key={i}
					cx={p.x}
					cy={p.y}
					r={px(2.4)}
					fill="#ffffff"
					stroke={color}
					strokeWidth={px(1.2)}
				/>
			))}
			{/* Широкая прозрачная линия — зона захвата, по тонкой не попасть */}
			<polyline
				data-role="cable"
				data-id={cableId}
				points={toPoints(route)}
				fill="none"
				stroke="transparent"
				strokeWidth={px(10)}
				strokeLinejoin="round"
				strokeLinecap="round"
			/>
		</g>
	);
};

interface DraftProps {
	/** От прибора через уже поставленные точки */
	route: Pt[];
	/** Куда ляжет следующий участок; null — курсор вне холста */
	preview: Pt | null;
	/** Курсор над прибором: кабель можно подключить */
	canFinish: boolean;
	color: string;
	mmPerPx: number;
}

export const CableDraft = ({ route, preview, canFinish, color, mmPerPx }: DraftProps) => {
	const px = (n: number) => n * mmPerPx;
	const full = preview ? [...route, preview] : route;
	return (
		<g style={{ pointerEvents: "none" }}>
			<polyline
				points={toPoints(full)}
				fill="none"
				stroke={canFinish ? SELECTED_COLOR : color}
				strokeWidth={px(1.7)}
				strokeLinejoin="round"
				strokeLinecap="round"
				strokeDasharray={`${px(5)} ${px(3)}`}
			/>
			{route.map((p, i) => (
				<circle
					key={i}
					cx={p.x}
					cy={p.y}
					r={px(2.4)}
					fill="#ffffff"
					stroke={color}
					strokeWidth={px(1.2)}
				/>
			))}
			{preview && (
				<circle
					cx={preview.x}
					cy={preview.y}
					r={px(canFinish ? 5 : 3)}
					fill={canFinish ? "#2f6feb22" : "#ffffff"}
					stroke={canFinish ? SELECTED_COLOR : color}
					strokeWidth={px(1.2)}
				/>
			)}
		</g>
	);
};
